import { Timestamp, collection, deleteDoc, doc, query, setDoc, updateDoc, where } from "firebase/firestore";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import { parseTrackerDocument } from "@/models/gps";
import {
  isGPSShareLinkExpired,
  isGPSShareLinkLive,
  makeGPSShareLink,
  parseGPSShareLink,
  gpsShareLinkFirestoreData,
  type GPSShareLink,
} from "@/models/gpsShareLink";
import type { GPSShareOptions } from "@/models/gpsShare";
import { gpsSharePublicURL } from "@/services/publicUrls";
import { useCompetitionDisplay } from "./useCompetitionDisplay";
import "@/features/home/home.css";

// Management screen for the public spectator link. Writes go straight to
// Firestore (the leagueOverlayOutputs precedent) and are validated by the lean
// gpsShareLinks rules; the toggles themselves are enforced server-side by the
// feed, so "Preview as spectator" is the only honest verification.

const OPTION_FIELDS: Array<{ key: keyof GPSShareOptions; label: string; hint: string }> = [
  { key: "showDeviceList", label: "Show the device list", hint: "Off leaves a bare map with markers only." },
  { key: "showCourse", label: "Show the course", hint: "Draws the event's KML course and placemarks." },
  { key: "showSpeed", label: "Show speed", hint: "Publishes each tracker's current mph." },
  { key: "showBattery", label: "Show battery", hint: "Publishes each tracker's battery percentage." },
  { key: "showCategory", label: "Show categories", hint: "Publishes each tracker's athlete category." },
  {
    key: "showDeviceIDs",
    label: "Show GPS IDs and aliases",
    hint: "Publishes operational identifiers such as “GPS 007” and “Moto 3”.",
  },
];

function localDateTimeValue(date: Date | null): string {
  if (!date) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function GpsShareSettings() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { currentUser } = useAuth();
  const { display, error: displayError } = useCompetitionDisplay(instanceId);
  const leagueID = display?.instance.leagueID ?? null;

  const linksQuery =
    leagueID && instanceId
      ? query(
        collection(db, "gpsShareLinks"),
        where("leagueID", "==", leagueID),
        where("competitionInstanceID", "==", instanceId),
      )
      : null;
  const links = useCollection(linksQuery, parseGPSShareLink, `gpsShareLinks:${leagueID}:${instanceId}`);

  const trackersQuery = instanceId
    ? query(collection(db, "leagueCompetitionInstances", instanceId, "gpsData"))
    : null;
  const trackers = useCollection(trackersQuery, parseTrackerDocument, `gpsShareTrackers:${instanceId}`);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFallbackURL, setCopyFallbackURL] = useState<string | null>(null);

  // A create race would leave two pointers; converge on the lowest doc id.
  const link = useMemo(
    () => [...links.data].sort((a, b) => a.id.localeCompare(b.id))[0] ?? null,
    [links.data],
  );

  const write = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The share link could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  const createLink = () =>
    write(async () => {
      if (!leagueID || !instanceId) throw new Error("Competition is still loading.");
      const now = new Date();
      const created = makeGPSShareLink({
        id: crypto.randomUUID(),
        leagueID,
        competitionInstanceID: instanceId,
        createdByUserID: currentUser?.id,
      });
      await setDoc(
        doc(db, "gpsShareLinks", created.id),
        gpsShareLinkFirestoreData({ ...created, createdAt: now, updatedAt: now }),
      );
    });

  const patch = (changes: Record<string, unknown>) =>
    write(async () => {
      if (!link) return;
      await updateDoc(doc(db, "gpsShareLinks", link.id), { ...changes, updatedAt: Timestamp.now() });
    });

  const deleteLink = () =>
    write(async () => {
      if (!link) return;
      await deleteDoc(doc(db, "gpsShareLinks", link.id));
    });

  const copyURL = async (target: GPSShareLink) => {
    const url = gpsSharePublicURL(target.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyFallbackURL(null);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFallbackURL(url);
    }
  };

  const excluded = new Set(link?.excludedUserIDs ?? []);
  const live = link ? isGPSShareLinkLive(link) : false;
  const expired = link ? isGPSShareLinkExpired(link) : false;

  return (
    <div className="home-screen">
      <h2>Share the live map</h2>
      <p className="home-hint">{display?.eventName ?? "Competition"}</p>
      <ErrorText>{displayError}</ErrorText>
      <ErrorText>{error}</ErrorText>
      <ErrorText>{links.error}</ErrorText>

      {links.loading && !link ? (
        <LoadingView />
      ) : !link ? (
        <SBCard className="home-card">
          <h3>No share link yet</h3>
          <p className="home-hint">
            Create a link anyone can open — no account needed — to follow this event's trackers on a live map. You
            choose what spectators see, and you can turn the link off at any time.
          </p>
          <PrimaryButton title="Create share link" onClick={() => void createLink()} disabled={busy || !leagueID} />
        </SBCard>
      ) : (
        <>
          <SBCard className="home-card">
            <h3>
              Public link{" "}
              <span className="badge">{live ? "Live" : expired ? "Expired" : "Off"}</span>
            </h3>
            <div className="global-output-row">
              <code>{gpsSharePublicURL(link.id)}</code>
              <SecondaryButton title={copied ? "Copied!" : "Copy link"} onClick={() => void copyURL(link)} />
            </div>
            {copyFallbackURL && <p className="home-hint">Copy blocked by the browser — select the URL above.</p>}
            <p className="home-hint">
              {live
                ? "This link is public right now. Anyone who has it can watch this event's trackers live."
                : expired
                  ? "This link has expired and serves nothing until you clear or extend the expiry."
                  : "This link is switched off and serves nothing until you turn it back on."}
            </p>
            <div className="global-output-row">
              <SecondaryButton
                title={link.enabled ? "Turn link off" : "Turn link on"}
                onClick={() => void patch({ enabled: !link.enabled })}
                disabled={busy}
              />
              <SecondaryButton
                title="Preview as spectator"
                onClick={() => window.open(gpsSharePublicURL(link.id), "_blank", "noopener")}
              />
            </div>
          </SBCard>

          <SBCard className="home-card">
            <h3>What spectators see</h3>
            <p className="home-hint">
              The map, athlete names and each tracker's active/stale status are always shown. Everything below is
              withheld from the public feed unless you switch it on.
            </p>
            {OPTION_FIELDS.map((field) => (
              <label key={field.key} className="gps-map-course-option">
                <input
                  type="checkbox"
                  checked={link.options[field.key]}
                  disabled={busy}
                  onChange={(event) =>
                    void patch({ options: { ...link.options, [field.key]: event.target.checked } })
                  }
                />
                <span>
                  {field.label}
                  <br />
                  <span className="home-hint">{field.hint}</span>
                </span>
              </label>
            ))}
          </SBCard>

          <SBCard className="home-card">
            <h3>Expiry</h3>
            <p className="home-hint">
              Optional. After this moment the link stops serving data — enforced on the server, not just hidden.
            </p>
            <input
              type="datetime-local"
              value={localDateTimeValue(link.expiresAt)}
              disabled={busy}
              onChange={(event) => {
                const value = event.target.value;
                const parsed = value ? new Date(value) : null;
                void patch({
                  expiresAt: parsed && !Number.isNaN(parsed.getTime()) ? Timestamp.fromDate(parsed) : null,
                });
              }}
            />
            {link.expiresAt && (
              <SecondaryButton title="Clear expiry" onClick={() => void patch({ expiresAt: null })} disabled={busy} />
            )}
          </SBCard>

          <SBCard className="home-card">
            <h3>Hide specific trackers</h3>
            <p className="home-hint">
              Excluded trackers are dropped from the public feed entirely — useful for marshal and staff vehicles.
            </p>
            <ErrorText>{trackers.error}</ErrorText>
            {trackers.loading && trackers.data.length === 0 ? (
              <LoadingView />
            ) : trackers.data.length === 0 ? (
              <p className="home-hint">No trackers have reported for this competition yet.</p>
            ) : (
              trackers.data.map((tracker) => (
                <label key={tracker.id} className="gps-map-course-option">
                  <input
                    type="checkbox"
                    checked={excluded.has(tracker.userID)}
                    disabled={busy}
                    onChange={(event) => {
                      const next = new Set(excluded);
                      if (event.target.checked) next.add(tracker.userID);
                      else next.delete(tracker.userID);
                      void patch({ excludedUserIDs: [...next] });
                    }}
                  />
                  <span>
                    Hide {tracker.gpsID ? `${tracker.gpsID} · ` : ""}
                    {tracker.displayName || tracker.gpsAlias || "Unnamed tracker"}
                    {tracker.athleteCat ? ` (${tracker.athleteCat})` : ""}
                  </span>
                </label>
              ))
            )}
          </SBCard>

          <SBCard className="home-card">
            <h3>Delete</h3>
            <p className="home-hint">
              Deleting revokes the link permanently. Anyone holding the URL will see a “no longer active” page.
            </p>
            <SecondaryButton title="Delete share link" onClick={() => void deleteLink()} disabled={busy} />
          </SBCard>
        </>
      )}
    </div>
  );
}
